/**
 * Time zones, and the rules about them that both sides have to agree on.
 *
 * The rules, in the order they matter:
 *
 *  1. **Stored values are instants.** Every timestamp crossing the API is a
 *     UTC ISO-8601 string produced from a `TIMESTAMPTZ`. A zone is never
 *     stored alongside it, because the instant is the fact and the zone is
 *     the reader's preference.
 *
 *  2. **Exports are UTC, always.** Evidence packs and CSV downloads ignore
 *     the reader's preference entirely. An audit trail whose times depend on
 *     who exported it is not evidence -- two auditors comparing two exports
 *     of the same period must see identical bytes.
 *
 *  3. **Rendered values carry their zone.** Every formatted timestamp in the
 *     UI is labelled. A screenshot of an incident timeline with no zone on it
 *     is a screenshot somebody will misread, and the cost of the label is
 *     eight characters.
 *
 * The zone itself is resolved in this order: the user's saved preference,
 * then the browser's own zone, then UTC. UTC last rather than a fixed local
 * zone, because a wrong-but-plausible local time is worse than an obviously
 * non-local one -- the second gets questioned.
 */

/**
 * The zone used when nothing else can be determined.
 *
 * Not `Australia/Sydney`. A fallback that looks like a local time is a
 * fallback nobody notices is wrong; one that says UTC is self-describing.
 */
export const FALLBACK_TIME_ZONE = 'UTC';

/**
 * Is this a zone name this runtime can actually format in?
 *
 * Asked of the runtime rather than checked against a bundled list, because a
 * bundled list is a list that goes stale every time the IANA database gains
 * a zone -- and the failure mode of a stale list is refusing a legitimate
 * preference from someone who has just moved country.
 *
 * `Intl.DateTimeFormat` throws a `RangeError` on an unknown zone, which is
 * the check; anything else propagates, because a non-RangeError here means
 * something is wrong with the runtime rather than with the input.
 */
export function isValidTimeZone(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0 || value.length > 64) return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: value }).format(0);
    return true;
  } catch (err) {
    if (err instanceof RangeError) return false;
    throw err;
  }
}

/**
 * The zone this runtime believes it is in.
 *
 * In a browser this is the operating system's zone, which is what "detect
 * the user's time zone" means in practice -- there is no more authoritative
 * source, and asking the user to pick one before they can read a date is a
 * worse experience than being right nine times out of ten.
 *
 * `resolvedOptions().timeZone` is specified to return an IANA name, but a
 * few older environments return `undefined` here, so it is validated rather
 * than trusted.
 */
export function detectTimeZone(): string {
  try {
    const detected = new Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimeZone(detected) ? detected : FALLBACK_TIME_ZONE;
  } catch {
    return FALLBACK_TIME_ZONE;
  }
}

/**
 * Picks the zone to render in.
 *
 * Takes both candidates rather than reaching for the browser itself, so the
 * precedence is one testable function instead of a rule re-implemented at
 * each call site. An invalid saved preference falls through to the browser
 * rather than erroring: the preference is presentation, and a user whose
 * saved zone was removed from the IANA database should see slightly wrong
 * times, not a blank page.
 */
export function resolveTimeZone(
  preference: string | null | undefined,
  browserZone: string | null | undefined,
): string {
  if (isValidTimeZone(preference)) return preference;
  if (isValidTimeZone(browserZone)) return browserZone;
  return FALLBACK_TIME_ZONE;
}

/**
 * A short label for the zone, as the user's locale writes it: `AEST`,
 * `AEDT`, `GMT+11`.
 *
 * Derived from the same formatter that renders the time, so the label is
 * correct for *that instant* rather than for now. During the hour after a
 * daylight-saving change, an event stamped `AEST` sitting above an event
 * stamped `AEDT` is the only thing that makes the ordering legible -- a
 * fixed label taken from the current date would put the same name on both
 * and make the timeline look as though it went backwards.
 */
export function zoneAbbreviation(instant: Date, timeZone: string, locale?: string): string {
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(instant);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? timeZone;
  } catch {
    return timeZone;
  }
}

/**
 * The parts of an instant as they read on a wall clock in a given zone.
 *
 * `en-CA` with numeric options because it produces zero-padded
 * `YYYY-MM-DD` ordering, which makes the assembly below a join rather than a
 * rearrangement. The locale is fixed on purpose -- this function's output is
 * a machine-readable value for an `<input>`, not something a user reads, so
 * it must not vary with the reader's locale.
 */
function wallClockParts(
  instant: Date,
  timeZone: string,
): { year: string; month: string; day: string; hour: string; minute: string } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(instant);

  const find = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '00';

  return {
    year: find('year'),
    month: find('month'),
    day: find('day'),
    // `hour12: false` yields 24 for midnight in some runtimes rather than 00.
    // Left as-is it produces `2026-01-01T24:00`, which an `<input>` rejects
    // silently -- the field just goes blank.
    hour: find('hour') === '24' ? '00' : find('hour'),
    minute: find('minute'),
  };
}

/**
 * An instant as the `YYYY-MM-DDTHH:mm` string a `datetime-local` input wants,
 * read in the given zone.
 *
 * `datetime-local` has no concept of a zone: it is a wall-clock reading. So
 * a filter field has to be drawn in the same zone the results are drawn in,
 * or the user sets a boundary at 9am and gets rows starting at 7am with
 * nothing on screen explaining why.
 */
export function formatWallClock(instant: Date, timeZone: string): string {
  const p = wallClockParts(instant, timeZone);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/**
 * How far ahead of UTC the zone is at this instant, in milliseconds.
 *
 * Measured rather than looked up: the same instant is rendered in the zone,
 * re-read as though those numbers were UTC, and the difference taken. This
 * is the only way to get an offset out of `Intl` without shipping a copy of
 * the IANA database, and it is exact for any instant.
 */
function zoneOffsetMs(instant: Date, timeZone: string): number {
  const p = wallClockParts(instant, timeZone);
  const asIfUtc = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour),
    Number(p.minute),
  );
  // The instant's own seconds are discarded by the formatter, so compare
  // against a minute-truncated instant rather than the raw one.
  const truncated = Math.floor(instant.getTime() / 60_000) * 60_000;
  return asIfUtc - truncated;
}

/**
 * The inverse: a `YYYY-MM-DDTHH:mm` wall-clock reading in a zone, back to the
 * instant it denotes.
 *
 * Two passes. The first treats the reading as UTC and subtracts the zone's
 * offset at that approximate instant; the second re-measures the offset at
 * the result. One correction is enough because a zone's offset changes by at
 * most a couple of hours and never twice within a day, so the first guess is
 * always within the same offset period as the answer -- except at a
 * transition, which is the next paragraph.
 *
 * In the hour that a zone repeats when clocks go back, a reading is genuinely
 * ambiguous and denotes two instants; this returns one of them. In the hour
 * that a zone skips when clocks go forward, a reading denotes none, and this
 * returns the instant the clock jumped to. Both are acceptable for a filter
 * boundary and neither would be acceptable for a stored value -- which is
 * why nothing stored ever goes through here.
 */
export function parseWallClock(value: string, timeZone: string): Date | null {
  const asUtc = Date.parse(`${value}:00.000Z`);
  if (Number.isNaN(asUtc)) return null;

  const firstGuess = new Date(asUtc - zoneOffsetMs(new Date(asUtc), timeZone));
  return new Date(asUtc - zoneOffsetMs(firstGuess, timeZone));
}

/** Zones offered in the settings picker, ahead of every other IANA name. */
export const SUGGESTED_TIME_ZONES: readonly string[] = [
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Adelaide',
  'Australia/Perth',
  'Australia/Darwin',
  'Australia/Hobart',
  'Pacific/Auckland',
  'Asia/Singapore',
  'Asia/Kolkata',
  'Europe/London',
  'America/New_York',
  'America/Los_Angeles',
  'UTC',
];

/**
 * Every zone this runtime knows, for the settings picker.
 *
 * `Intl.supportedValuesOf` is the only way to enumerate them without
 * shipping a copy of the IANA database, and it is absent in older runtimes,
 * so the suggestions above are the floor rather than a convenience. A picker
 * that offers fourteen sensible zones is usable; one that offers none is not.
 */
export function availableTimeZones(): readonly string[] {
  const supported =
    typeof Intl.supportedValuesOf === 'function'
      ? (Intl.supportedValuesOf('timeZone') as string[])
      : [];

  const merged = new Set<string>([...SUGGESTED_TIME_ZONES, ...supported]);
  return [...merged];
}
